// CRITICAL: Import React shim FIRST to ensure global availability
import "./react-shim.js";

import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";

// React global exposure is now handled centrally by react-shim.js

import { HelmetProvider } from "react-helmet-async";
import { observeLCP } from "./analytics/perf";
import { I18nProvider } from "./components/i18n";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import { initI18n } from "./i18n"; // Initialize i18n
import "./index.css";
import { SubscriptionProvider } from "./lib/SubscriptionContext";
import { PersonaProvider } from "./lib/brand/PersonaContext";
import { CultureThemeProvider } from "./lib/theme/CultureThemeProvider";
import { StreamThemeProvider } from "./lib/theme/StreamThemeProvider";
import { CalmProvider } from "./preferences/CalmModeContext";

// React polyfill already handled by react-shim.js import above
// Initialize React runtime monitoring for regression prevention
import "./utils/react-runtime-monitor";

// Ensure React development tools are available in development
if (import.meta.env.DEV) {
  // @ts-ignore - Ensure React DevTools hook exists to prevent errors
  if (!globalThis.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    // @ts-ignore
    globalThis.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
      isDisabled: true,
      supportsFiber: true,
      inject: () => { },
      onCommitFiberRoot: () => { },
      onCommitFiberUnmount: () => { },
    };
  }
}


// Global error handler for uncaught exceptions during module load
window.addEventListener('error', (event) => {
  const root = document.getElementById('root');
  if (root && root.innerHTML === '') {
    root.innerHTML = `
      <div style="padding: 20px; font-family: system-ui; color: #b91c1c; background: #fff1f2; border: 1px solid #fecaca; margin: 20px; border-radius: 8px;">
        <h2 style="margin-top: 0;">Uncaught Runtime Error</h2>
        <pre style="white-space: pre-wrap; font-size: 12px;">${event.message}\n${event.filename}:${event.lineno}</pre>
        <button onclick="window.location.reload()" style="margin-top: 10px; padding: 8px 16px; border: 1px solid #b91c1c; background: white; color: #b91c1c; border-radius: 4px; cursor: pointer;">Reload Application</button>
      </div>
    `;
  }
});

try {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("FATAL: Root element '#root' not found in DOM");
  }

  const root = ReactDOM.createRoot(rootElement);

  // Define bootstrap function
  const bootstrap = async function() {
    try {
      // Log bootstrap start for debugging
      console.log("🚀 Bootstrap starting...", {
        host: window.location.host,
        path: window.location.pathname,
        timestamp: new Date().toISOString()
      });

      // Run schema canary before heavy boot
      const { runSchemaCanary } = await import("@/lib/canary/schemaCanary");
      await runSchemaCanary().catch(err => console.warn("Canary failed:", err));

      // Initialize analytics performance monitoring
      observeLCP();

      // Initialize i18n system first
      await initI18n();

      // Dynamically import App to avoid any circular/TDZ issues during module init
      const { default: App } = await import("./App");

      console.log("✅ Initializing React app...");
      root.render(
        <React.StrictMode>
          <HelmetProvider>
            <CalmProvider>
              <PersonaProvider>
                <CultureThemeProvider>
                  <SubscriptionProvider
                    value={{ plan: "free", isPremium: false, region: "US" }}
                  >
                    <I18nProvider>
                      <AuthProvider>
                        <StreamThemeProvider>
                          <Suspense fallback={
                            <div className="min-h-screen flex items-center justify-center">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-gray-600">Loading application...</p>
                              </div>
                            </div>
                          }>
                            <Toaster />
                            <App />
                          </Suspense>
                        </StreamThemeProvider>
                      </AuthProvider>
                    </I18nProvider>
                  </SubscriptionProvider>
                </CultureThemeProvider>
              </PersonaProvider>
            </CalmProvider>
          </HelmetProvider>
        </React.StrictMode>,
      );
      console.log("✅ React app initialized");
    } catch (error) {
      console.error("❌ Fatal bootstrap error:", error);
      // Enhanced error UI so the page is not blank
      const message = error instanceof Error
        ? `${error.name}: ${error.message}\n\nStack:\n${error.stack || 'No stack trace'}`
        : String(error);
      
      rootElement.innerHTML =
        `<div style="font-family: ui-sans-serif, system-ui; padding: 24px; max-width: 800px; margin: 0 auto;">
        <h1 style="font-size: 24px; margin: 0 0 16px; color: #dc2626;">⚠️ App failed to start</h1>
        <pre style="white-space: pre-wrap; color: #b91c1c; background: #fef2f2; padding: 16px; border-radius: 8px; overflow-x: auto; font-size: 12px;">${message}</pre>
        <p style="color: #6b7280; margin-top: 16px;">Check the browser console (F12) for more details.</p>
        <button onclick="window.location.reload()" style="margin-top: 16px; padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">🔄 Reload Page</button>
      </div>`;
    }
  };

  // Execute bootstrap
  bootstrap().catch(err => {
    console.error("Unhandled bootstrap error:", err);
    rootElement.innerHTML = `<h1>Fatal Bootstrap Error</h1><pre>${err.message}</pre>`;
  });

} catch (e: any) {
  console.error("Top-level initialization error:", e);
  document.body.innerHTML = `
    <div style="padding: 20px; color: red; font-family: monospace;">
      <h1>Critical Initialization Error</h1>
      <p>${e.message}</p>
      <pre>${e.stack}</pre>
    </div>
  `;
}
